/**
 * Trang khuyen mai.
 *
 * Hai diem nghiep vu quyet dinh cach bay man hinh nay:
 *
 *   1. BNS-R05 — nhan khuyen mai la HANH DONG CHU DONG. Khong co chuyen tu gan
 *      thuong roi nguoi choi phat hien so du bi rang buoc vong cuoc luc di rut.
 *      Vi vay moi chuong trinh deu co nut nhan rieng, va sau khi nhan thi noi ro
 *      rang buoc keo theo.
 *   2. Thuong dang chay luon di kem TIEN DO VONG CUOC va SO CON THIEU. Chi hien
 *      so tien thuong ma khong hien dieu kien la nua su that.
 */

import type { JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, ApiError, type PromotionRow } from '../lib/api';
import { formatMoney } from '../lib/money';
import { useSession } from '../lib/session';
import { Badge, Button, Card, EmptyState, ErrorList, Skeleton } from '../components/ui';
import styles from './Promotions.module.css';

const TYPE_LABEL: Readonly<Record<string, string>> = {
  DEPOSIT_MATCH: 'Thưởng theo tiền nạp',
  NO_DEPOSIT: 'Thưởng không cần nạp',
  CASHBACK: 'Hoàn cược',
  LOGIN_STREAK: 'Điểm danh liên tục',
  FREE_SPINS: 'Lượt quay miễn phí',
  ODDS_BOOST: 'Tăng tỷ lệ',
  BET_INSURANCE: 'Bảo hiểm cược',
  TIER_GIFT: 'Quà theo hạng',
  LEADERBOARD: 'Bảng xếp hạng',
};

const TYPE_NOTE: Readonly<Record<string, string>> = {
  DEPOSIT_MATCH: 'Thưởng được cấp theo tỷ lệ trên số tiền nạp, kèm điều kiện vòng cược.',
  NO_DEPOSIT: 'Thưởng cấp trước, kèm điều kiện vòng cược và trần rút.',
  CASHBACK: 'Hoàn lại một phần số lỗ ròng của kỳ, tính khi kỳ chốt.',
  LOGIN_STREAK: 'Điểm danh mỗi ngày để cộng điểm; bỏ một ngày là chuỗi tính lại từ đầu.',
  BET_INSURANCE: 'Hoàn tiền cược khi phiếu hỏng đúng một chặng.',
};

export function PromotionsPage(): JSX.Element {
  const { isAuthenticated } = useSession();
  const queryClient = useQueryClient();

  const promotions = useQuery({
    queryKey: ['promotions'],
    queryFn: api.promotions.list,
    enabled: isAuthenticated,
  });

  const bonuses = useQuery({
    queryKey: ['bonuses'],
    queryFn: api.promotions.bonuses,
    enabled: isAuthenticated,
  });

  const loyalty = useQuery({
    queryKey: ['loyalty'],
    queryFn: api.promotions.loyalty,
    enabled: isAuthenticated,
  });

  if (!isAuthenticated) {
    return <EmptyState title="Đăng nhập để xem khuyến mại" />;
  }

  const tier = loyalty.data?.loyalty ?? null;

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Khuyến mại</h1>

      {/* --- Hang thanh vien --- */}
      {loyalty.isLoading ? (
        <Skeleton height={100} />
      ) : tier ? (
        <Card>
          <div className={styles.tier}>
            <div className={styles.tierTop}>
              <div>
                <span className={styles.tierLabel}>Hạng thành viên</span>
                <span className={styles.tierName}>{tier.tier_name ?? tier.tier_code}</span>
              </div>
              <span className={`numeric ${styles.tierPoints}`}>
                {Number(tier.points).toLocaleString('vi-VN')} điểm
              </span>
            </div>

            {/* Hien SO DIEM CON THIEU chu khong chi phan tram: nguoi choi can
                biet con bao nhieu nua, khong phai mot thanh mau. */}
            {tier.next_tier && (
              <div className={styles.tierNext}>
                <div className={styles.tierBar}>
                  <div
                    className={styles.tierFill}
                    style={{ width: `${tierPercent(tier.points, tier.next_tier.points_needed)}%` }}
                  />
                </div>
                <span className={styles.tierNextText}>
                  Còn{' '}
                  <strong className="numeric">
                    {Number(tier.next_tier.points_needed).toLocaleString('vi-VN')}
                  </strong>{' '}
                  điểm để lên hạng {tier.next_tier.name}
                </span>
              </div>
            )}
          </div>
        </Card>
      ) : null}

      {/* --- Thuong dang chay --- */}
      {bonuses.data && bonuses.data.bonuses.length > 0 && (
        <Card title="Thưởng đang chạy">
          <ul className={styles.bonusList}>
            {bonuses.data.bonuses.map((b) => {
              const required = BigInt(b.wagering_required.amount_minor);
              const progress = BigInt(b.wagering_progress.amount_minor);
              const percent = required > 0n ? Number((progress * 100n) / required) : 100;

              return (
                <li key={b.bonus_id} className={styles.bonus}>
                  <div className={styles.bonusTop}>
                    <span className={styles.bonusName}>{b.campaign_code}</span>
                    <span className={`numeric ${styles.bonusAmount}`}>
                      {formatMoney(b.amount)} {b.amount.currency}
                    </span>
                  </div>
                  <div className={styles.bonusBar}>
                    <div
                      className={styles.bonusFill}
                      style={{ width: `${Math.min(percent, 100)}%` }}
                    />
                  </div>
                  <span className={styles.bonusRemaining}>
                    Còn thiếu{' '}
                    <strong className="numeric">{formatMoney(b.wagering_remaining)}</strong>{' '}
                    {b.amount.currency} vòng cược
                  </span>
                </li>
              );
            })}
          </ul>
          <p className={styles.note}>
            Số dư thưởng dùng để cược được, chưa rút được cho tới khi hoàn thành vòng cược.{' '}
            <Link to="/vi" className={styles.link}>Xem ví</Link>
          </p>
        </Card>
      )}

      {/* --- Chuong trinh dang mo --- */}
      <Card title="Chương trình đang mở">
        {promotions.isLoading ? (
          <Skeleton height={120} />
        ) : promotions.data && promotions.data.promotions.length > 0 ? (
          <ul className={styles.promoList}>
            {promotions.data.promotions.map((p) => (
              <PromotionCard
                key={p.code}
                promotion={p}
                onDone={() => {
                  void queryClient.invalidateQueries({ queryKey: ['promotions'] });
                  void queryClient.invalidateQueries({ queryKey: ['bonuses'] });
                }}
              />
            ))}
          </ul>
        ) : (
          <p className={styles.muted}>Hiện chưa có chương trình nào áp dụng cho tài khoản này.</p>
        )}
      </Card>

      <p className={styles.footNote}>
        Nhận khuyến mại là lựa chọn chủ động. Thưởng đã nhận kéo theo điều kiện vòng cược và trần
        cược; điều kiện của từng khoản hiển thị ngay khi thưởng được cấp.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------

function PromotionCard({
  promotion,
  onDone,
}: {
  promotion: PromotionRow;
  onDone: () => void;
}): JSX.Element {
  const optIn = useMutation({
    mutationFn: () => api.promotions.optIn(promotion.code),
    onSuccess: onDone,
  });

  const claim = useMutation({
    mutationFn: () => api.promotions.claimStreak(promotion.code),
    onSuccess: onDone,
  });

  const error =
    optIn.error instanceof ApiError
      ? optIn.error
      : claim.error instanceof ApiError
        ? claim.error
        : null;

  const isStreak = promotion.type === 'LOGIN_STREAK';

  return (
    <li className={styles.promo}>
      <div className={styles.promoTop}>
        <div>
          <span className={styles.promoName}>{promotion.name}</span>
          {/* Loai chuong trinh hien KE CA khi da nhan: no cho biet thuong den tu
              dau va tinh ra sao, khong phai mot nhan trang thai tam thoi. */}
          <span className={styles.promoType}>{TYPE_LABEL[promotion.type] ?? promotion.type}</span>
        </div>
        {promotion.opted_in && <Badge tone="positive">Đã nhận</Badge>}
      </div>

      {TYPE_NOTE[promotion.type] && (
        <p className={styles.promoNote}>{TYPE_NOTE[promotion.type]}</p>
      )}

      <span className={styles.promoDates}>
        {promotion.ends_at
          ? `Áp dụng đến ${new Date(promotion.ends_at).toLocaleDateString('vi-VN')}`
          : 'Không giới hạn thời gian'}
      </span>

      {error && <ErrorList messages={error.messages} />}

      <div className={styles.promoActions}>
        {promotion.opted_in ? (
          // Chuong trinh diem danh can hanh dong MOI NGAY, khac cac loai chi
          // nhan mot lan — nen sau khi nhan van con nut bam.
          isStreak ? (
            <Button size="sm" loading={claim.isPending} onClick={() => claim.mutate()}>
              Điểm danh hôm nay
            </Button>
          ) : (
            <span className={styles.promoDone}>Đã tham gia chương trình này.</span>
          )
        ) : (
          <Button size="sm" loading={optIn.isPending} onClick={() => optIn.mutate()}>
            Nhận chương trình
          </Button>
        )}
      </div>

      {claim.data && (
        <p className={claim.data.claimed ? styles.claimOk : styles.promoNote}>
          {claim.data.claimed
            ? `Đã cộng ${Number(claim.data.points ?? 0).toLocaleString('vi-VN')} điểm.`
            : 'Hôm nay đã điểm danh rồi.'}
        </p>
      )}
    </li>
  );
}

/** Phan tram tien toi hang ke tiep, tinh tu so diem con thieu. */
function tierPercent(points: string, needed: string): number {
  const current = Number(points);
  const remaining = Number(needed);
  const target = current + remaining;
  if (target <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((current / target) * 100)));
}
